import React, { useState } from 'react';
import { Avatar, Box, Divider, IconButton, Menu, MenuItem, Skeleton, Tooltip } from '@mui/material';
import { Check, KeyboardArrowDown, KeyboardArrowUp, Language, Logout } from '@mui/icons-material';
import { endSession } from '../utils/axios';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import OrgSwitcher from './OrgSwitcher';
import { useTranslatedMessages } from '../hooks/useTranslatedMessages';
import { getLanguageConfig, LANGUAGE_OPTIONS } from '../constants/appConstants';
import { setLanguage } from '../api/ui/languageSlice';

const UserAccount = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [anchorEl, setAnchorEl] = useState(null);
  const [languageOpen, setLanguageOpen] = useState(false);
  const open = Boolean(anchorEl);
  const { user } = useSelector((state) => state.user);
  const { language, common } = useTranslatedMessages();
  const profile = common.profile ?? {};
  const currentLanguage = LANGUAGE_OPTIONS.find((option) => option.code === language) ?? LANGUAGE_OPTIONS[0];

  const handleLogout = () => {
    queryClient.clear();
    endSession().finally(() => {
      navigate('/login', { replace: true });
    });
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setLanguageOpen(false);
  };

  const handleLanguageSelect = (code) => {
    dispatch(setLanguage(code));
  };

  function stringToColor(string) {
    let hash = 0;
    let i;

    /* eslint-disable no-bitwise */
    for (i = 0; i < string.length; i += 1) {
      hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }

    let color = '#';

    for (i = 0; i < 3; i += 1) {
      const value = (hash >> (i * 8)) & 0xff;
      color += `00${value.toString(16)}`.slice(-2);
    }
    /* eslint-enable no-bitwise */

    return color;
  }

  function stringAvatar(name) {
    let initials = `${name.split(' ')[0][0]}`;
    if (name.split(' ').length > 1) {
      initials += `${name.split(' ')[1][0]}`;
    }

    return {
      sx: {
        bgcolor: stringToColor(name),
      },
      children: initials,
    };
  }

  if (!user?.name) {
    return <Skeleton variant="circular" width={40} height={40} />;
  }

  return (
    <Box sx={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 2 }}>
      <OrgSwitcher />
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            textAlign: 'right',
          }}
        >
          <span style={{ fontSize: '1rem', fontWeight: 'bold' }}>{user?.name}</span>
          <span style={{ fontSize: '1rem', color: '#888' }}>{user?.email}</span>
        </Box>
        <Tooltip title={profile.account_settings ?? 'Account settings'}>
          <IconButton
            onClick={handleClick}
            size="small"
            aria-controls={open ? 'account-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
          >
            <Avatar sx={{ width: 32, height: 32 }} {...stringAvatar(user?.name)} />
          </IconButton>
        </Tooltip>
      </Box>
      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        open={open}
        onClose={handleClose}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              overflow: 'visible',
              minWidth: 280,
              filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
              mt: 1.5,
              '&::before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                bgcolor: 'background.paper',
                transform: 'translateY(-50%) rotate(45deg)',
                zIndex: 0,
              },
            },
          },
          list: {
            dense: true,
            disablePadding: true,
            sx: {
              py: 0,
              '& .MuiMenuItem-root + .MuiDivider-root': {
                marginTop: 0,
                marginBottom: 0,
              },
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem
          disabled
          dense={false}
          disableGutters
          sx={{
            opacity: 1,
            p: 0,
            m: 0,
            minHeight: '0 !important',
            height: 'auto',
            cursor: 'default',
            pointerEvents: 'none',
            '&.Mui-disabled': { opacity: 1 },
            '& + .MuiDivider-root': {
              marginTop: 0,
              marginBottom: 0,
            },
          }}
        >
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: 0.25,
              px: 2,
              py: 1,
              lineHeight: 1.2,
              fontSize: '0.875rem',
            }}
          >
            <Box component="span" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              {user?.name}
            </Box>
            <Box component="span" sx={{ color: 'text.secondary', lineHeight: 1.2 }}>
              {user?.email}
            </Box>
          </Box>
        </MenuItem>
        <Divider component="li" sx={{ m: '0 !important' }} />
        <Box component="li" sx={{ listStyle: 'none' }}>
          <Box
            component="button"
            type="button"
            onClick={() => setLanguageOpen((current) => !current)}
            aria-expanded={languageOpen}
            aria-label={profile.language ?? 'Language'}
            sx={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              px: 2,
              py: 1.25,
              border: 0,
              bgcolor: 'action.hover',
              cursor: 'pointer',
              font: 'inherit',
              color: 'text.primary',
              textAlign: 'start',
            }}
          >
            <Language sx={{ color: 'action.active', fontSize: 18, flexShrink: 0 }} />
            <Box component="span" sx={{ flex: 1, fontSize: '0.875rem', lineHeight: 1 }}>
              {profile.language ?? 'Language'}
            </Box>
            <Box
              component="span"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.25,
                color: 'text.secondary',
                fontSize: '0.875rem',
                lineHeight: 1,
              }}
            >
              {currentLanguage.nativeLabel}
              {languageOpen ? <KeyboardArrowUp sx={{ fontSize: 18 }} /> : <KeyboardArrowDown sx={{ fontSize: 18 }} />}
            </Box>
          </Box>
          {languageOpen &&
            LANGUAGE_OPTIONS.map((option) => {
              const selected = option.code === language;

              return (
                <Box
                  key={option.code}
                  component="button"
                  type="button"
                  lang={getLanguageConfig(option.code).htmlLang}
                  onClick={() => handleLanguageSelect(option.code)}
                  aria-label={option.nativeLabel}
                  aria-current={selected ? 'true' : undefined}
                  sx={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    px: 2,
                    py: 1,
                    border: 0,
                    bgcolor: 'transparent',
                    cursor: 'pointer',
                    font: 'inherit',
                    color: 'text.primary',
                    textAlign: 'start',
                  }}
                >
                  <Box component="span" sx={{ width: 28, flexShrink: 0, color: 'text.secondary', fontSize: '0.75rem' }}>
                    {option.codeLabel}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box component="span" sx={{ display: 'block', fontSize: '0.875rem', lineHeight: 1.2 }}>
                      {option.nativeLabel}
                    </Box>
                    {option.englishLabel !== option.nativeLabel && (
                      <Box
                        component="span"
                        sx={{ display: 'block', fontSize: '0.75rem', color: 'text.secondary', lineHeight: 1.2 }}
                      >
                        {option.englishLabel}
                      </Box>
                    )}
                  </Box>
                  {selected && <Check sx={{ color: 'primary.main', fontSize: 18, flexShrink: 0 }} />}
                </Box>
              );
            })}
        </Box>
        <Divider component="li" sx={{ m: '0 !important' }} />
        <MenuItem
          dense={false}
          disableGutters
          onClick={handleLogout}
          sx={{
            color: 'error.main',
            p: 0,
            m: 0,
            minHeight: '0 !important',
            height: 'auto',
            borderRadius: 0,
          }}
        >
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              px: 2,
              py: 1,
              lineHeight: 1,
              fontSize: '0.875rem',
            }}
          >
            <Logout sx={{ fontSize: 18, flexShrink: 0 }} />
            <Box component="span" sx={{ lineHeight: 1 }}>
              {profile.logout ?? 'Logout'}
            </Box>
          </Box>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default UserAccount;
